/**
 * Logger Service
 *
 * This services will be able to be injected and used from
 * anywhere in the site, has the following log functions:
 *
 * 'insertEvent': This functions logs information into the insert event service on server.
 */

// imports
import log4javascript from 'log4javascript';

const loggerService = ($log, httpService, API) => {
  // Defines path elements for host
  const _protocol = 'http://';
  const _host = 'localhost';
  const _port = '3000';

  /**
   * Main logger variable for 'insertEvent' functions, is initialized when function is called
   */
  let loggerInsertEvent;

  /**
   * Main appender variable for 'loggerInsertEvent', it's created or redefined with function is called
   */
  let loggerInsertEventAppender;

  /**
   * Defining API paths to call by ajaxAppender
   */
  const _eventInsertPath = '/event/insert'; // Service path for insert event end point

  /**
   * Function 'getRemoteCallLogger'
   * Sets 'log4javascript' base configurations and returns an instance
   * of root logger without any appenders or default configurations
   */
  const getRemoteCallLogger = () => {
    log4javascript.resetConfiguration();
    log4javascript.logLog.setQuietMode(true);
    return log4javascript.getRootLogger();
  };

  /**
   * Function 'getCustomAjaxAppender'
   * Generates a custom AjaxAppender instance for API service logging functions
   *
   * @param requestPath [String] -> API service path where logging will be addressed to.
   * @param params [Object] -> Object with attributes which will be included as sending params.
   * @param callback [Function] (Optional) -> Callback function which will receive the API response and can process it.
   * @returns {log4javascript.AjaxAppender}
   */
  const getCustomAjaxAppender = (requestPath, params, callback) => {
    let ajaxAppender = new log4javascript.AjaxAppender(requestPath);
    ajaxAppender.setWaitForResponse(true);
    ajaxAppender.addHeader('Content-Type', 'application/json;charset=utf-8');

    // Obtains user token for session and includes it on request header
    ajaxAppender.addHeader(API.getTokenCode(), httpService.getToken());
    let layout = new log4javascript.JsonLayout(false, false);

    for (let key in params) {
      layout.setCustomField(key, params[key]);
    }
    ajaxAppender.setLayout(layout);

    if (callback) {
      ajaxAppender.setRequestSuccessCallback(callback);
    }

    // Sets default fail callback which prints error details on console and set logger vars to null
    ajaxAppender.setFailCallback(defaultFailCallBack);

    return ajaxAppender;
  };

  /**
   * Function 'insertEventDefaultCallback'
   * Default function which will be set as callback if no callback defined when 'insertEvent' function is called.
   * Verifies if response is sent by the insert event service and prints on console if response has Success or Failure code.
   *
   * @param data [Object] -> Response object sent by API service
   */
  const insertEventDefaultCallback = (data) => {
    try {
      var result = JSON.parse(data.response);
      if ((result.status) && (result.status.code === 200)) {
        // $log.info('Insert event was sent successfully');
      }
    } catch (err) {
        // $log.error('No response from insert event call');
    }
  };

  /**
   * Function 'defaultFailCallBack'
   * When API call is made by AjaxAppender and an failure response returns
   * this function will notify via console and print error details
   */
  const defaultFailCallBack = (error) => {
    $log.error('Logging error: ', error);

    // Set AjaxAppender related logger vars to null, since responses with error codes crashes them and need to be initialized again
    loggerInsertEvent = null;
    loggerInsertEventAppender = null;
  };

  return Object.freeze({
    /**
     * Service's function 'insertEvent': Logs information into the insert event service on server,
     * all event parameters (start with 'event') are required and are wrapped to be send to service,
     * callback parameters it's optional.
     *
     * @param eventKey [String] -> (Sample: 'Search')
     * @param eventTimestamp [String] -> (Sample: '2016-04-02 22:24:44.322')
     * @param eventSource [String] -> (Sample: 'Order Component')
     * @param eventMessage [Object] -> (Sample: {"ordernumber": "abc%", "location": "main hosptial", "date": "2015-01-01 00:00:00.000"})
     * @param eventResult [Array] -> (Sample: ["order1id","order2id","order3id"])
     * @param callback [Function] (Optional) -> Function to be called once API service returns a response.
     */
    insertEvent: (eventKey, eventTimestamp, eventSource, eventMessage, eventResult, callback) => {
      // Validation: 'eventMessage' has to be an object, because it will be converted to JSON before being sent as param
      if (typeof eventMessage !== 'object') {
        $log.error('LoggerService.insertEvent(): \'message\' has to be an object');
        return;
      }

      // Creates the API event insert service path from defined constants
      const requestPath = `${_protocol}${_host}:${_port}${_eventInsertPath}`;

      let eventInsertParams = {
        eventKey: eventKey,
        eventTimestamp: eventTimestamp,
        eventSource: eventSource,
        eventMessage: JSON.stringify(eventMessage),
        eventResult: eventResult
      };

      // Verifies if 'loggerInsertEvent' logger variable is already created, otherwise creates it
      if (!loggerInsertEvent) {
        loggerInsertEvent = getRemoteCallLogger(requestPath);
      } else {
        // Removing appender in order to set a new one with new sending parameters
        loggerInsertEvent.removeAppender(loggerInsertEventAppender);
      }

      // Defining appender with sending parameters and appending it to looger object
      loggerInsertEventAppender = getCustomAjaxAppender(requestPath, eventInsertParams, callback || insertEventDefaultCallback);
      loggerInsertEvent.addAppender(loggerInsertEventAppender);

      // Calls to the logging function on info level. AjaxAppender will process the API service call,
      // send the params and process the callback (if defined)
      loggerInsertEvent.info();
    }
  });
};

loggerService.$inject = ['$log', 'httpService', 'API'];
export {loggerService};



/** WEBPACK FOOTER **
 ** ./src/app/shared/services/logger.service.js
 **/